import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { LogOut, Menu, ChevronDown, Wallet, Folder, type LucideIcon } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { useLogout } from '@/features/auth/use-auth';
import { usePlatformContext } from '@/features/platform/use-context';
import { iconFor } from '@/features/platform/icon-map';
import { TourLauncher } from '@/features/onboarding/TourLauncher';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { BrandLogo } from '@/shared/brand/BrandLogo';
import { cn } from '@/lib/utils';

interface NavEntry {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  group?: string;
}

// Iconos de las cabeceras de grupo (submenús del sidebar).
const GROUP_ICON: Record<string, LucideIcon> = { Finanzas: Wallet };

// Fallback estatico (solo core) mientras carga /me/context. Evita un sidebar
// vacio en el primer render; en cuanto llega el contexto, el nav es data-driven.
const FALLBACK_NAV: NavEntry[] = [
  { to: '/', label: 'Dashboard', icon: iconFor('dashboard'), end: true },
  { to: '/pos', label: 'Punto de venta', icon: iconFor('pos') },
  { to: '/cash-sessions', label: 'Caja', icon: iconFor('wallet') },
  { to: '/documents', label: 'Comprobantes', icon: iconFor('file') },
  { to: '/parties', label: 'Clientes', icon: iconFor('users') },
  { to: '/products', label: 'Productos', icon: iconFor('package') },
  { to: '/purchases', label: 'Compras', icon: iconFor('shopping-bag') },
  { to: '/receivables', label: 'Por cobrar', icon: iconFor('coins'), group: 'Finanzas' },
  { to: '/payables', label: 'Por pagar', icon: iconFor('credit-card'), group: 'Finanzas' },
  { to: '/treasury', label: 'Bancos y caja', icon: iconFor('bank'), group: 'Finanzas' },
  { to: '/expenses', label: 'Gastos', icon: iconFor('expense'), group: 'Finanzas' },
  { to: '/reports', label: 'Reportes', icon: iconFor('chart') },
  { to: '/settings', label: 'Configuracion', icon: iconFor('settings') },
];

// Items "Sistema" core que siempre deben estar disponibles (aunque el backend
// no los liste en /me/context). Se añaden al final si faltan.
const SYSTEM_DEFAULTS: NavEntry[] = [
  { to: '/reports', label: 'Reportes', icon: iconFor('chart') },
  { to: '/settings', label: 'Configuración', icon: iconFor('settings') },
];

function ensureSystem(nav: NavEntry[]): NavEntry[] {
  const out = [...nav];
  for (const d of SYSTEM_DEFAULTS) {
    if (!out.some((n) => n.to === d.to)) out.push(d);
  }
  return out;
}

// Secciones del menú (como el diseño): Operación · Finanzas · Sistema.
const OPERACION = ['/pos', '/cash-sessions', '/documents', '/parties', '/products', '/purchases', '/inventory'];
const SISTEMA = ['/reports', '/settings', '/marketplace'];
const SECTION_ORDER = ['Operación', 'Finanzas', 'Sistema'] as const;

function sectionOf(item: NavEntry): 'top' | 'Operación' | 'Finanzas' | 'Sistema' {
  if (item.to === '/') return 'top';
  if (item.group === 'Finanzas') return 'Finanzas';
  if (SISTEMA.some((p) => item.to === p || item.to.startsWith(p + '/'))) return 'Sistema';
  if (OPERACION.some((p) => item.to === p || item.to.startsWith(p + '/'))) return 'Operación';
  return 'Operación';
}

/** Agrupa el nav en: items superiores (Dashboard) + secciones en orden fijo. */
function planNav(nav: NavEntry[]): { top: NavEntry[]; sections: Record<string, NavEntry[]> } {
  const top: NavEntry[] = [];
  const sections: Record<string, NavEntry[]> = { 'Operación': [], 'Finanzas': [], 'Sistema': [] };
  for (const item of nav) {
    const s = sectionOf(item);
    if (s === 'top') top.push(item);
    else sections[s]!.push(item);
  }
  return { top, sections };
}

interface SidebarContentProps {
  onNavigate?: () => void;
  nav: NavEntry[];
  user: ReturnType<typeof useAuthStore.getState>['user'];
  activeTenantName: string | null;
  initial: string;
  onLogout: () => void;
  logoutPending: boolean;
}

function SidebarContent({
  onNavigate,
  nav,
  user,
  activeTenantName,
  initial,
  onLogout,
  logoutPending,
}: SidebarContentProps): JSX.Element {
  return (
    <div className="h-full flex flex-col bg-sidebar text-sidebar-foreground">
      <div className="px-5 h-20 flex items-center border-b border-sidebar-border shrink-0">
        <BrandLogo variant="wordmark-light" height={40} />
      </div>

      <nav className="flex-1 p-7 space-y-1 overflow-y-auto">
        {(() => {
          const { top, sections } = planNav(nav);
          return (
            <>
              {top.map((item) => (
                <NavItemLink key={item.to} item={item} onNavigate={onNavigate} />
              ))}
              {SECTION_ORDER.map((sec) => {
                const items = sections[sec] ?? [];
                if (items.length === 0) return null;
                if (sec === 'Finanzas') return <NavGroup key={sec} name="Finanzas" items={items} onNavigate={onNavigate} />;
                return (
                  <div key={sec} className="pt-3">
                    <div className="px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.07em] text-white/35">{sec}</div>
                    {items.map((item) => (
                      <NavItemLink key={item.to} item={item} onNavigate={onNavigate} />
                    ))}
                  </div>
                );
              })}
            </>
          );
        })()}
      </nav>

      <div className="p-7 border-t border-sidebar-border shrink-0">
        <div className="flex items-center gap-3 px-2 py-2 mb-1">
          <div className="h-9 w-9 rounded-full bg-sidebar-accent text-sidebar-accent-foreground flex items-center justify-center text-sm font-semibold">
            {initial}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate text-white">{user?.name}</p>
            {activeTenantName && (
              <p className="text-[11px] text-sidebar-foreground truncate">{activeTenantName}</p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onLogout}
          disabled={logoutPending}
          className="w-full flex items-center gap-2 px-3 py-3 sm:py-2 text-base sm:text-sm rounded-xl text-sidebar-foreground hover:bg-white/5 hover:text-white transition-colors"
        >
          <LogOut className="h-4 w-4" />
          Cerrar sesion
        </button>
      </div>
    </div>
  );
}

function NavItemLink({
  item,
  onNavigate,
  nested,
}: {
  item: NavEntry;
  onNavigate?: () => void;
  nested?: boolean;
}): JSX.Element {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      data-tour={item.to === '/' ? 'dashboard' : item.to.replace(/^\//, '')}
      className={({ isActive }) =>
        cn(
          'group flex items-center gap-3 rounded-xl text-base sm:text-sm font-medium transition-all',
          nested ? 'px-3 py-2.5 sm:py-2' : 'px-3.5 py-3 sm:py-2.5',
          isActive
            ? 'bg-sidebar-accent text-sidebar-accent-foreground shadow-md shadow-primary/30'
            : 'text-sidebar-foreground hover:bg-white/5 hover:text-white',
        )
      }
    >
      {({ isActive }) => (
        <>
          <item.icon
            className={cn('h-5 w-5 sm:h-[18px] sm:w-[18px] shrink-0 transition-transform', isActive ? '' : 'group-hover:scale-110')}
            strokeWidth={isActive ? 2.4 : 2}
          />
          <span>{item.label}</span>
        </>
      )}
    </NavLink>
  );
}

function NavGroup({
  name,
  items,
  onNavigate,
}: {
  name: string;
  items: NavEntry[];
  onNavigate?: () => void;
}): JSX.Element {
  const location = useLocation();
  const containsActive = items.some((i) => location.pathname.startsWith(i.to));
  const storageKey = `nav-group-${name}`;
  // Por defecto EXPANDIDO (recuerda la preferencia del usuario en localStorage).
  const [open, setOpen] = useState<boolean>(() => {
    try {
      const v = window.localStorage.getItem(storageKey);
      return v === null ? true : v === '1';
    } catch {
      return true;
    }
  });
  // Auto-expandir si navego a una pantalla del grupo (no esconder el item activo).
  useEffect(() => {
    if (containsActive) setOpen(true);
  }, [containsActive]);
  const toggle = () =>
    setOpen((o) => {
      const next = !o;
      try {
        window.localStorage.setItem(storageKey, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  const GroupIcon = GROUP_ICON[name] ?? Folder;
  return (
    <div>
      <button
        type="button"
        onClick={toggle}
        className="group flex w-full items-center gap-3 rounded-xl px-3.5 py-3 sm:py-2.5 text-base sm:text-sm font-medium text-sidebar-foreground hover:bg-white/5 hover:text-white transition-all"
      >
        <GroupIcon className="h-5 w-5 sm:h-[18px] sm:w-[18px] shrink-0" strokeWidth={2} />
        <span className="flex-1 text-left">{name}</span>
        <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="mt-0.5 ml-4 pl-2 border-l border-sidebar-border space-y-0.5">
          {items.map((item) => (
            <NavItemLink key={item.to} item={item} onNavigate={onNavigate} nested />
          ))}
        </div>
      )}
    </div>
  );
}

export function AppShell(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const activeTenantId = useAuthStore((s) => s.activeTenantId);
  const logout = useLogout();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Nav data-driven: viene de los modulos activos del tenant (/me/context).
  // Mientras carga, usa el fallback estatico (solo core) para no parpadear.
  const ctx = usePlatformContext();
  const nav: NavEntry[] = ensureSystem(
    ctx.data
      ? ctx.data.nav.map((n) => ({ to: n.to, label: n.label, icon: iconFor(n.icon), end: n.end, group: n.group }))
      : FALLBACK_NAV,
  );

  // Cerrar drawer mobile cuando cambia la ruta
  const activeTenant = user?.memberships.find((m) => m.tenantId === activeTenantId);
  const initial = (user?.name ?? '?').charAt(0).toUpperCase();

  // Titulo dinamico para el topbar mobile (mejor que "fact" siempre)
  const currentNav = nav.find((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to) && n.to !== '/'));

  return (
    <div className="app-canvas min-h-screen lg:h-screen lg:overflow-hidden flex lg:p-3 lg:gap-3">
      {/* Sidebar FIJO en desktop (≥lg): ocupa toda la altura, solo el contenido scrollea */}
      <aside className="hidden lg:flex w-64 shrink-0 rounded-2xl overflow-hidden">
        <SidebarContent
          nav={nav}
          user={user}
          activeTenantName={activeTenant?.tenantName ?? null}
          initial={initial}
          onLogout={() => logout.mutate()}
          logoutPending={logout.isPending}
        />
      </aside>

      {/* Container principal: en mobile incluye topbar; en desktop solo el outlet */}
      <div className="flex-1 min-w-0 flex flex-col lg:min-h-0">
        {/* Topbar mobile (<lg): logo + menu burger */}
        <header className="lg:hidden sticky top-0 z-30 flex items-center gap-3 h-14 px-4 bg-card border-b border-border">
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                aria-label="Abrir menu"
                className="-ml-2 inline-flex h-11 w-11 items-center justify-center rounded-md text-foreground hover:bg-accent transition-colors"
              >
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72 max-w-[85vw]" hideClose>
              <SidebarContent
                onNavigate={() => setMobileNavOpen(false)}
                nav={nav}
                user={user}
                activeTenantName={activeTenant?.tenantName ?? null}
                initial={initial}
                onLogout={() => logout.mutate()}
                logoutPending={logout.isPending}
              />
            </SheetContent>
          </Sheet>

          <div className="flex items-center gap-2 min-w-0">
            <BrandLogo variant="icon" height={28} />
            <span className="text-base font-semibold tracking-tight truncate">
              {currentNav?.label ?? 'Cajita'}
            </span>
          </div>
        </header>

        <main className="flex-1 min-w-0 lg:min-h-0 lg:rounded-2xl bg-card lg:border lg:border-border overflow-x-hidden lg:overflow-y-auto lg:shadow-sm">
          <Outlet />
        </main>
      </div>

      <TourLauncher />
    </div>
  );
}
